import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { preRegistrationApi } from '../api/pre-registration.api';
import { preRegistrationKeys } from './usePreRegistrationQueries';
import type { OfficialAccommodationMatch } from '../types/official-accommodation.types';

// The endpoint accepts 2 characters; 3 avoids noisy results while typing
export const LOOKUP_MIN_LENGTH = 3;
const LOOKUP_LIMIT = 10;
const LOOKUP_DEBOUNCE_MS = 300;

export interface RankedOfficialMatch extends OfficialAccommodationMatch {
  sameDepartment: boolean;
}

// Matches of the list's department first; within each group the backend order is kept
const rankByDepartment = (
  matches: OfficialAccommodationMatch[],
  departmentId?: number
): RankedOfficialMatch[] => {
  const ranked = matches.map((match) => ({
    ...match,
    sameDepartment: departmentId !== undefined && match.department?.id === departmentId,
  }));
  return [...ranked.filter((m) => m.sameDepartment), ...ranked.filter((m) => !m.sameDepartment)];
};

// Search-as-you-type over the official REPAT registry (GET /api/accommodations/lookup)
export const useOfficialAccommodationLookup = (text: string, departmentId?: number) => {
  const typed = text.trim();
  const q = useDebouncedValue(typed, LOOKUP_DEBOUNCE_MS);
  const enabled = q.length >= LOOKUP_MIN_LENGTH;
  const isIdle = typed.length < LOOKUP_MIN_LENGTH;

  // Keyed by the text: responses of older texts never replace the current one
  const query = useQuery({
    queryKey: preRegistrationKeys.officialLookup(q),
    queryFn: () => preRegistrationApi.lookupOfficialAccommodations(q, LOOKUP_LIMIT),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const matches = useMemo(
    () => (!isIdle && enabled && query.data ? rankByDepartment(query.data, departmentId) : []),
    [isIdle, enabled, query.data, departmentId]
  );

  return {
    matches,
    // The text is too short to search
    isIdle,
    // Debouncing or waiting for the response of the current text
    isSearching: !isIdle && (q !== typed || query.isFetching),
    isError: !isIdle && enabled && query.isError,
    // A full page means there may be more: the user should refine the text
    isLimitReached: matches.length >= LOOKUP_LIMIT,
  };
};
